import { COLONY_SHIP_ID } from "@kaeliux/shared";
import { extractBuildingLevels } from "./buildings.js";
import { deliverCargoToPlanet, dockFleetShips } from "./fleetResolution.js";
/**
 * Whether `step`'s completion condition is already satisfied by `context`.
 * `manual` steps never self-complete — they only advance via the UI's
 * Next/Finish button — everything else lets the tutorial auto-advance the
 * moment the player performs the real action being taught.
 */
export function isTutorialStepComplete(step, context) {
    switch (step.completion.type) {
        case "manual":
            return false;
        case "buildingLevel":
            return (context.buildingLevels[step.completion.building] ?? 0) >= step.completion.minLevel;
        case "allBuildingLevels":
            return step.completion.requirements.every((req) => (context.buildingLevels[req.building] ?? 0) >= req.minLevel);
        case "scienceLevel":
            return (context.scienceLevels[step.completion.science] ?? 0) >= step.completion.minLevel;
        case "shipBuilt":
            return (context.shipCounts[step.completion.shipId] ?? 0) >= step.completion.minCount;
        case "shipUpgradeLevel":
            return (context.shipUpgradeLevels[step.completion.shipId] ?? 0) >= step.completion.minLevel;
        case "fleetsSent":
            return context.sentFleetMissions?.includes(step.completion.missionType) ?? false;
        case "allianceJoined":
            return context.team?.inAlliance ?? false;
        case "teamPlanetFounded":
            return context.team?.hasTeamPlanet ?? false;
        case "sunsControlled":
            return (context.team?.controlledSuns ?? 0) >= step.completion.minCount;
        case "roundWon":
            return context.team?.wonRound ?? false;
    }
}
/**
 * Whether judging `step` needs the player's alliance state. Callers load it
 * only while a step in range reports true, so a player still on the early
 * tutorial never pays for alliance reads.
 */
export function tutorialStepNeedsTeam(step) {
    switch (step.completion.type) {
        case "allianceJoined":
        case "teamPlanetFounded":
        case "sunsControlled":
        case "roundWon":
            return true;
        case "fleetsSent":
            return step.completion.missionType === "sunAttack";
        default:
            return false;
    }
}
/**
 * Assembles a {@link TutorialProgressContext} from the planet the player is
 * on plus their player-wide levels. Shared by the client overlay (to gate
 * Next) and the server (to verify a reward claim) so both judge a step the
 * same way. A `colonize` flight counts as sent while a colony ship is away
 * from `planet`, once the player owns a second planet, or when the server has
 * recorded one (`colonizeFlightSent`). A `sunAttack` counts as sent once the
 * player's alliance has ever dispatched one (`team.sunAttackSent`).
 */
export function tutorialProgressFromState(input) {
    const { planet, sciences, shipUpgrades, planetCount, colonizeFlightSent, team } = input;
    const colonizeSent = (planet?.shipsInFlight?.[COLONY_SHIP_ID] ?? 0) > 0 || planetCount >= 2 || colonizeFlightSent === true;
    const sentFleetMissions = [];
    if (colonizeSent)
        sentFleetMissions.push("colonize");
    if (team?.sunAttackSent)
        sentFleetMissions.push("sunAttack");
    return {
        buildingLevels: planet ? extractBuildingLevels(planet.buildings) : {},
        scienceLevels: sciences ?? {},
        shipCounts: planet?.ships ?? {},
        shipUpgradeLevels: shipUpgrades ?? {},
        sentFleetMissions,
        ...(team
            ? {
                team: {
                    inAlliance: team.inAlliance,
                    hasTeamPlanet: team.hasTeamPlanet,
                    controlledSuns: team.controlledSuns,
                    wonRound: team.wonRound,
                },
            }
            : {}),
    };
}
/**
 * Credits a `resources` or `ships` tutorial reward to `planet`. Metal,
 * minerals and gas are capped at storage capacity like every other credit
 * path (the tick would clip anything above it anyway); energy is a flow
 * resource and is never granted. A `premium` reward belongs to the player,
 * not a planet, so the planet comes back unchanged.
 */
export function applyTutorialReward(planet, reward) {
    switch (reward.type) {
        case "resources":
            return deliverCargoToPlanet(planet, {
                metal: reward.amounts.metal ?? 0,
                minerals: reward.amounts.minerals ?? 0,
                gas: reward.amounts.gas ?? 0,
                scrap: 0,
            }).planet;
        case "ships": {
            const ships = {};
            for (const { shipId, quantity } of reward.ships) {
                ships[shipId] = (ships[shipId] ?? 0) + quantity;
            }
            return dockFleetShips(planet, ships);
        }
        case "premium":
            return planet;
    }
}
/**
 * The first tutorial step whose `resources`/`ships` reward can still be paid.
 * Rewards before it were never paid when the player passed those steps, and
 * replaying the tutorial must not pay them out now. Pinned once (to the step
 * recorded before the player's first progress write or claim) and stored as
 * `tutorialRewardFloor`, because `tutorialStepIndex` itself moves backwards on
 * Back or a restart. A player who never touched the tutorial starts at 0.
 */
export function tutorialRewardFloor(player) {
    return player.tutorialRewardFloor ?? player.tutorialStepIndex ?? 0;
}
/**
 * The furthest tutorial step the player has legitimately reached, stored as
 * `tutorialFurthestStep`. Unlike `tutorialStepIndex` it never moves backwards,
 * so it is the baseline the server checks a requested step against. A player
 * from before the field existed defaults to their recorded step; a player who
 * never touched the tutorial starts at 0.
 */
export function tutorialFurthestStep(player) {
    return player.tutorialFurthestStep ?? player.tutorialStepIndex ?? 0;
}
/**
 * Whether a player whose furthest step is `furthest` may be at step `target`.
 * Going back, or to the furthest step itself, is always allowed. Going forward
 * is allowed only when every step from `furthest` up to (not including)
 * `target` is manual or already complete against `context`, and `target` is a
 * real step. The server uses it for both progress writes and reward claims, so
 * a forged step index cannot skip a step the player has not done.
 */
export function canReachTutorialStep(steps, furthest, target, context) {
    if (target <= furthest)
        return true;
    if (target >= steps.length)
        return false;
    return steps
        .slice(furthest, target)
        .every((step) => step.completion.type === "manual" || isTutorialStepComplete(step, context));
}
//# sourceMappingURL=tutorial.js.map